import { useEffect, useState } from 'react';
import { useThemeStore } from '../../stores/themeStore';
import { LogOut, Shield, ChevronDown, Moon, Sun } from 'lucide-react';
import { useAuthStore } from '../../stores/auth';

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const setIsAdmin = useThemeStore((state) => state.setIsAdmin);
  const toggleTheme = useThemeStore((state) => state.toggleTheme);
  const theme = useThemeStore((state) => state.theme);
  const { user, logout } = useAuthStore();

  const isDark = theme === 'dark';

  const handleLogout = async () => {
    setIsProfileOpen(false);
    await logout();
  };

  useEffect(() => {
    setIsAdmin(true);
    return () => setIsAdmin(false);
  }, [setIsAdmin]);

  const getInitials = () => {
    if (!user?.firstName || !user?.lastName) return 'AD';
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  };

  const getFullName = () => {
    if (!user?.firstName || !user?.lastName) return 'Admin';
    return `${user.firstName} ${user.lastName}`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark 
        ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
        : 'bg-gradient-to-br from-slate-50 via-white to-slate-100'
    }`}>
      {/* Fixed Header */}
      <nav className={`sticky top-0 z-30 backdrop-blur-xl border-b transition-colors ${
        isDark 
          ? 'bg-slate-900/80 border-white/10' 
          : 'bg-white/80 border-slate-200'
      }`}>
        <div className="px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            {/* Logo / Title */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/25">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>Admin Panel</h1>
                <p className={`text-xs hidden sm:block ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Manage your platform</p>
              </div>
            </div>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className={`p-2.5 rounded-xl border transition-all ${
                  isDark 
                    ? 'bg-white/5 border-white/10 hover:bg-white/10 text-slate-400 hover:text-white' 
                    : 'bg-slate-50 border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-slate-700'
                }`}
              >
                {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>

              {/* Profile Menu */}
              <div className="relative">
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className={`flex items-center gap-2 p-1.5 pr-3 rounded-xl border transition-all ${
                    isDark 
                      ? 'bg-white/5 border-white/10 hover:bg-white/10' 
                      : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                  }`}
                >
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                    {getInitials()}
                  </div>
                  <span className={`text-sm hidden sm:inline ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{getFullName()}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${isProfileOpen ? 'rotate-180' : ''} ${isDark ? 'text-slate-400' : 'text-slate-500'}`} />
                </button>

                {isProfileOpen && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setIsProfileOpen(false)} 
                    />
                    <div className={`absolute right-0 mt-2 w-64 backdrop-blur-xl rounded-2xl shadow-2xl border py-2 z-50 overflow-hidden ${
                      isDark 
                        ? 'bg-slate-800/95 border-white/10' 
                        : 'bg-white border-slate-200'
                    }`}>
                      <div className={`px-4 py-3 border-b ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>{getFullName()}</p>
                        <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{user?.email || 'No email'}</p>
                      </div>

                      <div className="p-2">
                        <button
                          onClick={toggleTheme}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-colors ${
                            isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
                          }`}
                        >
                          <div className={`flex items-center gap-3 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                            {isDark ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
                            <span className="text-sm">Theme</span>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-lg ${
                            isDark 
                              ? 'bg-white/10 text-slate-400' 
                              : 'bg-slate-100 text-slate-500'
                          }`}>
                            {isDark ? 'Dark' : 'Light'}
                          </span>
                        </button>
                      </div>

                      <div className={`border-t p-2 ${isDark ? 'border-white/10' : 'border-slate-100'}`}>
                        <button 
                          onClick={handleLogout}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-red-500 transition-colors ${
                            isDark ? 'hover:bg-red-500/10' : 'hover:bg-red-50'
                          }`}
                        >
                          <LogOut className="w-4 h-4" />
                          <span className="text-sm">Sign out</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative">
        {children}
      </main>
    </div>
  );
};
